import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  BarChart3, TrendingUp, Package, Trash2, DollarSign,
  Download, FileText, Calendar, RefreshCw, Receipt, Building2, Users, Search, X,
  Clock, CalendarDays, UserCheck, AlertTriangle, Activity, Plus, ClipboardPaste, Edit3, ChevronDown, ChevronUp
} from 'lucide-react';
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ComposedChart
} from 'recharts';
import toast from 'react-hot-toast';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import api from '../api/axios';
import DashboardDrillDown from '../components/DashboardDrillDown';
import OrderReceiptModal from '../components/OrderReceiptModal';
import SortableHeader from '../components/SortableHeader';
import UiDateRangeBlock from '../components/ui/UiDateRangeBlock';
import { UiTabs } from '../components/ui/UiTabs';
import UiDateField from '../components/ui/UiDateField';
import UiTimeField from '../components/ui/UiTimeField';
import useSortable from '../hooks/useSortable';
import SalesReport from './reports/SalesReport';
import ExpensesReport from './reports/ExpensesReport';
import InventoryReport from './reports/InventoryReport';
import ProfitReport from './reports/ProfitReport';
import { formatApiError } from '../utils/apiErrors';
import { buildStandardPresetOptions } from '../utils/dateRangeUi';
import useDateFilter from '../hooks/useDateFilter';
import { formatDate } from '../utils/dateFormat';
import { roundSalaryDisplayAmount } from '../utils/payroll';
import {
  formatUiCurrencyCompact,
  formatUiDate,
  formatUiDateTimeRange,
  formatUiDateTime,
  formatUiHourLabel,
  formatUiMonthYear,
} from '../utils/uiFormat';

const COLORS = ['#f97316', '#22c55e', '#3b82f6', '#ef4444', '#8b5cf6', '#ec4899'];
const TIME_SLOT_COLORS = ['#fbbf24', '#f97316', '#ef4444', '#6366f1'];
const formatDateValueLocal = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const parseLocalDateValue = (dateStr) => new Date(`${dateStr}T00:00:00`);
const formatShortDateLabel = (dateStr) => formatUiDate(dateStr);
const formatFullDateLabel = (dateStr) => formatUiDate(dateStr);
const addCalendarDays = (dateStr, days) => {
  const date = new Date(`${dateStr}T12:00:00`);
  date.setDate(date.getDate() + days);
  return formatDateValueLocal(date);
};
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const parseAxisDate = (value) => {
  let s = String(value).substring(0, 10);
  if (/^\d{4}-\d{2}$/.test(s)) s = `${s}-01`;
  const d = parseLocalDateValue(s);
  return (d && !isNaN(d.getTime())) ? d : null;
};
// Consistent x-axis tick labels for all date/month range charts:
//   month/week -> label every Monday's date, ticks only in between
//   year       -> label Jan / Apr / Jul / Oct / Dec, ticks only in between
const formatRangeDateTick = (value, datePreset) => {
  const d = parseAxisDate(value);
  if (!d) return '';
  if (datePreset === 'year') {
    return (d.getDate() === 1 && [0, 3, 6, 9, 11].includes(d.getMonth())) ? MONTH_ABBR[d.getMonth()] : '';
  }
  return d.getDay() === 1 ? formatUiDate(String(value).substring(0, 10)) : '';
};

const REPORT_TABS = [
  { id: 'sales', label: 'Sales Report', icon: TrendingUp },
  { id: 'expenses', label: 'Expense Report', icon: Receipt },
  { id: 'profit', label: 'Profit & Loss', icon: DollarSign },
  { id: 'inventory', label: 'Inventory Report', icon: Package },
];

const Reports = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const requestedReport = new URLSearchParams(location.search).get('tab');
  const [activeReport, setActiveReport] = useState(() => (
    REPORT_TABS.some(report => report.id === requestedReport) ? requestedReport : 'sales'
  ));
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [businessDayStartHour, setBusinessDayStartHour] = useState(6);
  
  // Standardized date filter hook
  const {
    preset: datePreset,
    setPreset: setDatePreset,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    shiftRange,
    applyPreset,
    getPeriodParts,
    PRESETS: DATE_PRESETS,
  } = useDateFilter('today', businessDayStartHour);
  const periodParts = getPeriodParts();
  const filterEndCalendarDate = endDate ? addCalendarDays(endDate, 1) : '';
  
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [menuItemFilter, setMenuItemFilter] = useState('');
  const [modifierFilter, setModifierFilter] = useState('');
  const [modifierOptionFilter, setModifierOptionFilter] = useState('');
  const [servingSizeFilter, setServingSizeFilter] = useState('');
  const [servingSizeOptions, setServingSizeOptions] = useState([]);
  const [salesSearchQuery, setSalesSearchQuery] = useState('');
  const [debouncedSalesSearch, setDebouncedSalesSearch] = useState('');
  const fetchSeq = useRef(0);
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [modifiers, setModifiers] = useState([]);
  const [drillDown, setDrillDown] = useState({ open: false, type: '', params: {}, title: '' });
  const [chartTable, setChartTable] = useState({ open: false, title: '', data: [], summary: {}, onRowClick: null });
  const [staffMembers, setStaffMembers] = useState([]);
  const [staffMemberFilter, setStaffMemberFilter] = useState('');
  const [reportSettings, setReportSettings] = useState(null);
  const [historicalOrder, setHistoricalOrder] = useState(null);
  const [expandedPrevDay, setExpandedPrevDay] = useState(null);
  const { sortKey: salesSearchSortKey, sortDir: salesSearchSortDir, handleSort: handleSalesSearchSort, sortData: sortSalesSearchRows } = useSortable('total', 'desc');
  const [profitMode, setProfitMode] = useState('inventory'); // 'inventory' | 'expense'
  const [expensePLPeriod, setExpensePLPeriod] = useState('monthly'); // 'monthly' | 'yearly'
  const [expensePLData, setExpensePLData] = useState(null);
  const [expensePLLoading, setExpensePLLoading] = useState(false);

  // Previous Sales state
  const [showPreviousSales, setShowPreviousSales] = useState(false);
  const [previousSales, setPreviousSales] = useState([]);
  const [previousSalesLoading, setPreviousSalesLoading] = useState(false);
  const [showAddPrevSale, setShowAddPrevSale] = useState(false);
  const [prevSaleDate, setPrevSaleDate] = useState('');
  const [prevSaleTime, setPrevSaleTime] = useState('12:00');
  const [prevSaleItems, setPrevSaleItems] = useState([{ menu_item_id: '', quantity: 1, unit_price: '' }]);
  const [prevSalePayment, setPrevSalePayment] = useState('cash');
  const [prevSaleSubmitting, setPrevSaleSubmitting] = useState(false);
  // Smart Paste state
  const [showSmartPaste, setShowSmartPaste] = useState(false);
  const [smartPasteText, setSmartPasteText] = useState('');
  const [smartPasteDate, setSmartPasteDate] = useState('');
  const [smartParsedRows, setSmartParsedRows] = useState([]);
  const [smartPasteMenuItems, setSmartPasteMenuItems] = useState([]);
  const [smartParsing, setSmartParsing] = useState(false);
  const [smartSubmitting, setSmartSubmitting] = useState(false);

  const reportModifierOptions = useMemo(() => {
    if (!menuItemFilter) return modifiers;
    const selectedItem = menuItems.find(item => String(item.id) === String(menuItemFilter));
    const linkedModifiers = selectedItem?.modifiers || [];
    if (!linkedModifiers.length) return [];
    return linkedModifiers
      .map(link => link.modifier_detail || {
        id: link.modifier,
        name: link.modifier_name,
        display_name: link.modifier_name,
      })
      .filter(modifier => modifier?.id);
  }, [menuItemFilter, menuItems, modifiers]);

  const selectedReportModifier = useMemo(
    () => reportModifierOptions.find(modifier => String(modifier.id) === String(modifierFilter)) || null,
    [reportModifierOptions, modifierFilter]
  );

  const reportModifierOptionOptions = useMemo(
    () => (selectedReportModifier?.options || []).filter(option => option?.is_active !== false && option?.is_available !== false),
    [selectedReportModifier]
  );
  const salesSearchRows = useMemo(() => {
    const rows = data?.search_results || [];
    return sortSalesSearchRows(rows, (row, key) => {
      if (key === 'modifierLabelsText') return (row.modifier_labels || []).join(', ');
      return row[key];
    });
  }, [data, sortSalesSearchRows]);
  const normalizedHourlySales = useMemo(() => {
    // Order the 24 hours starting at the business-day start (e.g. 06:00 → 05:00 next day),
    // filling any missing hour with 0 so the x-axis is a consistent 3-hour-bucket window.
    const startHour = Number(businessDayStartHour) || 0;
    const byHour = new Map((data?.hourly_sales || []).map((row) => [Number(row.hour ?? row.display), row]));
    const out = [];
    for (let i = 0; i < 24; i += 1) {
      const h = (startHour + i) % 24;
      const row = byHour.get(h) || {};
      out.push({ ...row, hour: h, total: parseFloat(row.total || 0), display: formatUiHourLabel(h) });
    }
    return out;
  }, [data, businessDayStartHour]);
  const expenseDailyTrend = useMemo(() => {
    if (activeReport !== 'expenses') return [];
    if (!startDate || !endDate) return [];

    const dailyTrendMap = new Map((data?.daily_trend || []).map(item => [item.date, item]));
    const monthlyTrendMap = new Map(
      (data?.monthly_trend || []).map(item => [String(item.month || '').slice(0, 7), item])
    );

    if (datePreset === 'year') {
      const start = parseLocalDateValue(startDate);
      const end = parseLocalDateValue(endDate);
      const points = [];
      const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
      while (cursor <= end) {
        const monthKey = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`;
        const existing = monthlyTrendMap.get(monthKey);
        points.push({
          label: formatUiMonthYear(cursor),
          bucket_type: 'month',
          bucket_value: monthKey,
          total: parseFloat(existing?.total || 0),
          count: existing?.count || 0,
        });
        cursor.setMonth(cursor.getMonth() + 1);
      }
      return points;
    }

    const start = parseLocalDateValue(startDate);
    const end = datePreset === 'month'
      ? new Date(start.getFullYear(), start.getMonth() + 1, 0)
      : parseLocalDateValue(endDate);

    const points = [];
    let cursor = new Date(start);
    while (cursor <= end) {
      const dateKey = formatDateValueLocal(cursor);
      const existing = dailyTrendMap.get(dateKey);
      points.push({
        date: dateKey,
        label: formatUiDate(dateKey),
        bucket_type: 'day',
        bucket_value: dateKey,
        total: parseFloat(existing?.total || 0),
        count: existing?.count || 0,
      });
      cursor.setDate(cursor.getDate() + 1);
    }
    return points;
  }, [activeReport, data, datePreset, startDate, endDate]);

  const reconciliationViewModel = useMemo(() => {
    if (activeReport !== 'reconciliation' || !data) return null;
    const cash = data.cash_reconciliation || {};
    const payments = data.payment_breakdown || {};
    const sales = data.sales_summary || {};
    const finalSummary = data.final_summary || {};
    return {
      reportDate: data.report_date,
      openingBalance: parseFloat(cash.opening_cash || 0),
      totalSales: parseFloat(sales.total_sales || cash.total_sales || 0),
      cashSales: parseFloat(payments.cash_sales || cash.cash_sales || 0),
      digitalSales: parseFloat(payments.digital_sales || 0),
      cardSales: 0,
      variance: parseFloat(finalSummary.variance || 0),
      totalOrders: sales.order_count || 0,
      totalItemsSold: (data.category_tree || []).reduce((sum, category) => (
        sum + (category.items || []).reduce((itemSum, item) => itemSum + parseFloat(item.quantity || 0), 0)
      ), 0),
      expectedClosing: parseFloat(finalSummary.expected_cash_in_hand || cash.total_available_cash || 0),
      actualCash: finalSummary.actual_cash_in_hand,
      withdrawals: parseFloat(cash.total_withdrawals || payments.total_withdrawals || 0),
      salesByCategory: sales.by_category || [],
    };
  }, [activeReport, data]);

  const openInventoryPurchaseInExpenses = (row) => {
    const params = new URLSearchParams({
      tab: 'purchases',
      view: 'table',
      start_date: startDate,
      end_date: endDate,
    });
    if (row?.raw_material_id) params.set('item_id', String(row.raw_material_id));
    if (row?.expense_number) params.set('search', row.expense_number);
    else if (row?.item_name) params.set('search', row.item_name);
    navigate(`/expenses?${params.toString()}`);
  };

  const openInventoryItemPage = (row) => {
    const params = new URLSearchParams();
    if (row?.raw_material_id) params.set('item_id', String(row.raw_material_id));
    if (row?.item_name) params.set('search', row.item_name);
    navigate(`/inventory?${params.toString()}`);
  };

  const handleReportDrillDownRowClick = (row) => {
    if (drillDown.type === 'expense_category' || drillDown.type === 'expense_daily') {
      if (row?.raw_material_id || row?.item_name) openInventoryPurchaseInExpenses(row);
      return;
    }
    if (drillDown.type === 'inventory_purchase_history') {
      if (row?.raw_material_id || row?.item_name) openInventoryItemPage(row);
    }
  };

  const openOrderFromNumber = async (orderNumber) => {
    try {
      const res = await api.get(`/orders/?order_number=${orderNumber}&page_size=1`);
      const orders = res.data.results || res.data;
      if (!orders.length) { toast.error('Order not found'); return; }
      const order = orders[0];
      const receiptRes = await api.get(`/orders/${order.id}/receipt/`);
      setHistoricalOrder(receiptRes.data.order || order);
    } catch {
      toast.error('Could not load order details');
    }
  };

  const openDrillDown = (type, extraParams = {}, title = '') => {
    const params = activeReport === 'sales'
      ? { start_date: startDate, end_date: filterEndCalendarDate, start_time: startTime, end_time: endTime, ...extraParams }
      : { start_date: startDate, end_date: endDate, ...extraParams };
    if (departmentFilter) params.department_id = departmentFilter;
    if (categoryFilter) params.category_id = categoryFilter;
    if (menuItemFilter) params.menu_item_id = menuItemFilter;
    if (modifierFilter) params.modifier_group_id = modifierFilter;
    if (modifierOptionFilter) params.modifier_option_id = modifierOptionFilter;
    if (servingSizeFilter) params.serving_size = servingSizeFilter;
    if (activeReport === 'sales' && debouncedSalesSearch) params.search = debouncedSalesSearch;
    setDrillDown({ open: true, type, params, title });
  };
  const closeDrillDown = () => setDrillDown({ open: false, type: '', params: {}, title: '' });

  const openChartTable = (title, rawData, keys, onRowClick = null) => {
    const tableData = rawData.map(row => {
      const obj = {};
      keys.forEach(k => { obj[k] = row[k]; });
      // Preserve original row reference so onRowClick can access full data (e.g., hour, date)
      obj.__raw = row;
      return obj;
    });
    setChartTable({ open: true, title, data: tableData, summary: {}, onRowClick });
  };
  const closeChartTable = () => setChartTable({ open: false, title: '', data: [], summary: {}, onRowClick: null });

  const applyDatePreset = (preset) => {
    applyPreset(preset);
  };

  const selectedRangeLabel = useMemo(() => {
    return formatUiDateTimeRange(startDate, startTime, filterEndCalendarDate, endTime);
  }, [startDate, filterEndCalendarDate, startTime, endTime]);

  const reports = REPORT_TABS;

  useEffect(() => {
    if (requestedReport && REPORT_TABS.some(report => report.id === requestedReport)) {
      setActiveReport(requestedReport);
      return;
    }
    if (!requestedReport) return;
    const params = new URLSearchParams(location.search);
    params.set('tab', 'sales');
    navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true });
  }, [location.pathname, location.search, navigate, requestedReport]);

  useEffect(() => {
    api.get('/settings/').then(r => setReportSettings(r.data)).catch(() => {});
  }, []);

  // Debounce the sales search so a heavy report refetch fires once after typing settles.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSalesSearch(salesSearchQuery.trim()), 350);
    return () => clearTimeout(t);
  }, [salesSearchQuery]);

  useEffect(() => {
    fetchReport();
  }, [activeReport, startDate, endDate, startTime, endTime, departmentFilter, categoryFilter, menuItemFilter, modifierFilter, modifierOptionFilter, servingSizeFilter, debouncedSalesSearch, staffMemberFilter]);

  useEffect(() => {
    if (modifierOptionFilter && !reportModifierOptionOptions.some(option => String(option.id) === String(modifierOptionFilter))) {
      setModifierOptionFilter('');
    }
  }, [modifierOptionFilter, reportModifierOptionOptions]);

  useEffect(() => {
    api.get('/departments/?page_size=500').then(res => setDepartments(res.data.results || res.data)).catch(() => {});
    api.get('/categories/?page_size=500').then(res => setCategories(res.data.results || res.data)).catch(() => {});
    api.get('/menu-items/?page_size=500&active=true').then(res => setMenuItems(res.data.results || res.data)).catch(() => {});
    api.get('/modifiers/?page_size=500&active=true').then(res => setModifiers(res.data.results || res.data)).catch(() => {});
    api.get('/serving-size-options/?page_size=500').then(res => setServingSizeOptions(res.data.results || res.data)).catch(() => {});
    api.get('/staff/?page_size=500').then(res => setStaffMembers(res.data.results || res.data)).catch(() => {});
    api.get('/settings/').then(res => {
      const nextHour = res.data?.business_day_start_hour ?? 6;
      setBusinessDayStartHour(nextHour);
    }).catch(() => {});
  }, []);

  const STAFF_SUBREPORT_ENDPOINTS = {
    staff_attendance: () => {
      let u = `/staff-attendance/?start_date=${startDate}&end_date=${endDate}&page_size=2000`;
      if (staffMemberFilter) u += `&staff_id=${staffMemberFilter}`;
      return u;
    },
    staff_salary: () => {
      let u = `/reports/staff/?start_date=${startDate}&end_date=${endDate}`;
      if (departmentFilter) u += `&department_id=${departmentFilter}`;
      return u;
    },
    staff_leaves: () => {
      let u = `/staff-leaves/?start_date=${startDate}&end_date=${endDate}&page_size=2000`;
      if (staffMemberFilter) u += `&staff_id=${staffMemberFilter}`;
      return u;
    },
    reconciliation: () => {
      return `/reports/reconciliation/?date=${endDate}&start_date=${startDate}&end_date=${endDate}`;
    },
  };

  const fetchReport = async () => {
    if (activeReport === 'performance' || activeReport === 'stock_suggestion') return;
    const seq = ++fetchSeq.current;
    setLoading(true);
    try {
      let url;
      const isStaffSubreport = STAFF_SUBREPORT_ENDPOINTS[activeReport];
      if (isStaffSubreport) {
        url = isStaffSubreport();
      } else {
        const reportEndDate = activeReport === 'sales' ? filterEndCalendarDate : endDate;
        url = `/reports/${activeReport}/?start_date=${startDate}&end_date=${reportEndDate}`;
        if (activeReport === 'sales' && startTime) url += `&start_time=${startTime}`;
        if (activeReport === 'sales' && endTime) url += `&end_time=${endTime}`;
        if (activeReport !== 'sales' && datePreset === 'custom' && startTime) url += `&start_time=${startTime}`;
        if (activeReport !== 'sales' && datePreset === 'custom' && endTime) url += `&end_time=${endTime}`;
        if (departmentFilter) url += `&department_id=${departmentFilter}`;
        if (categoryFilter) url += `&category_id=${categoryFilter}`;
        if (menuItemFilter) url += `&menu_item_id=${menuItemFilter}`;
        if (modifierFilter) url += `&modifier_group_id=${modifierFilter}`;
        if (modifierOptionFilter) url += `&modifier_option_id=${modifierOptionFilter}`;
        if (servingSizeFilter) url += `&serving_size=${encodeURIComponent(servingSizeFilter)}`;
        if (activeReport === 'sales' && debouncedSalesSearch) url += `&search=${encodeURIComponent(debouncedSalesSearch)}`;
      }
      const res = await api.get(url);
      if (seq !== fetchSeq.current) return; // a newer request superseded this one — ignore stale data
      if (activeReport === 'staff_attendance' || activeReport === 'staff_leaves') {
        setData(res.data.results || res.data);
      } else {
        setData(res.data);
      }
    } catch (error) {
      if (seq !== fetchSeq.current) return;
      console.error('Error fetching report:', error);
      toast.error(formatApiError(error, 'Failed to load report'));
    } finally {
      if (seq === fetchSeq.current) setLoading(false);
    }
  };

  // Previous Sales handlers
  const fetchPreviousSales = async () => {
    setPreviousSalesLoading(true);
    try {
      const res = await api.get(`/orders/?previous_only=1&start_date=${startDate}&end_date=${endDate}&page_size=500`);
      setPreviousSales(res.data.results || res.data || []);
    } catch {
      toast.error('Failed to load previous sales');
    } finally {
      setPreviousSalesLoading(false);
    }
  };

  useEffect(() => {
    if (showPreviousSales && activeReport === 'sales') fetchPreviousSales();
  }, [showPreviousSales, activeReport, startDate, endDate]);

  // Summary of previous sales: category-wise totals + day-wise totals (instead of the full order list).
  const previousSalesSummary = useMemo(() => {
    const menuById = new Map(menuItems.map(m => [String(m.id), m]));
    const byCategory = new Map();
    const byDay = new Map();
    let grandTotal = 0;
    (previousSales || []).forEach(order => {
      const orderTotal = parseFloat(order.total_amount) || 0;
      grandTotal += orderTotal;
      const day = (order.created_at || '').slice(0, 10);
      if (day) {
        const d = byDay.get(day) || { date: day, orders: 0, total: 0 };
        d.orders += 1; d.total += orderTotal;
        byDay.set(day, d);
      }
      (order.items || []).forEach(it => {
        const miId = String(it.menu_item?.id || it.menu_item || it.menu_item_id || '');
        const mi = menuById.get(miId);
        const cat = it.menu_item_category_name || mi?.category_name || mi?.category?.name || 'Uncategorized';
        const qty = parseFloat(it.quantity) || 0;
        const lineTotal = parseFloat(it.total_price) || (parseFloat(it.unit_price || 0) * qty);
        const c = byCategory.get(cat) || { category: cat, quantity: 0, total: 0 };
        c.quantity += qty; c.total += lineTotal;
        byCategory.set(cat, c);
      });
    });
    return {
      categories: [...byCategory.values()].sort((a, b) => b.total - a.total),
      days: [...byDay.values()].sort((a, b) => b.date.localeCompare(a.date)),
      grandTotal,
      orderCount: (previousSales || []).length,
    };
  }, [previousSales, menuItems]);

  const deletePreviousSale = async (orderId) => {
    if (!window.confirm('Delete this previous sale record?')) return;
    try {
      await api.delete(`/orders/${orderId}/delete_previous_sale/`);
      toast.success('Previous sale deleted');
      setPreviousSales(prev => prev.filter(o => o.id !== orderId));
      fetchReport();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to delete'));
    }
  };

  const viewPreviousSaleReceipt = async (order) => {
    try {
      const res = await api.get(`/orders/${order.id}/receipt/`);
      setHistoricalOrder(res.data.order || order);
    } catch {
      setHistoricalOrder(order);
    }
  };

  const addPrevSaleItem = () => {
    setPrevSaleItems(prev => [...prev, { menu_item_id: '', quantity: 1, unit_price: '' }]);
  };

  const updatePrevSaleItem = (index, field, value) => {
    setPrevSaleItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'menu_item_id' && value) {
        const mi = menuItems.find(m => String(m.id) === String(value));
        if (mi && !updated[index].unit_price) updated[index].unit_price = mi.selling_price;
      }
      return updated;
    });
  };

  const removePrevSaleItem = (index) => {
    setPrevSaleItems(prev => prev.length > 1 ? prev.filter((_, i) => i !== index) : prev);
  };

  const submitPreviousSale = async () => {
    if (!prevSaleDate) { toast.error('Select a date'); return; }
    const validItems = prevSaleItems.filter(it => it.menu_item_id);
    if (!validItems.length) { toast.error('Add at least one item'); return; }
    setPrevSaleSubmitting(true);
    try {
      await api.post('/orders/add_previous_sale/', {
        sale_date: prevSaleDate,
        sale_time: prevSaleTime || '12:00',
        payment_method: prevSalePayment,
        items: validItems.map(it => ({
          menu_item_id: parseInt(it.menu_item_id),
          quantity: parseInt(it.quantity) || 1,
          unit_price: parseFloat(it.unit_price) || undefined,
        })),
      });
      toast.success('Previous sale added');
      setShowAddPrevSale(false);
      setPrevSaleItems([{ menu_item_id: '', quantity: 1, unit_price: '' }]);
      setPrevSaleDate('');
      setPrevSaleTime('12:00');
      fetchPreviousSales();
      fetchReport();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to add sale'));
    } finally {
      setPrevSaleSubmitting(false);
    }
  };

  // Smart Paste handlers
  const parseSmartPaste = async () => {
    if (!smartPasteText.trim()) { toast.error('Paste some text first'); return; }
    setSmartParsing(true);
    try {
      const res = await api.post('/orders/parse_smart_paste/', {
        text: smartPasteText,
        sale_date: smartPasteDate,
      });
      setSmartParsedRows(res.data.parsed || []);
      setSmartPasteMenuItems(res.data.menu_items || []);
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to parse'));
    } finally {
      setSmartParsing(false);
    }
  };

  const updateSmartRow = (index, field, value) => {
    setSmartParsedRows(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'matched_id' && value) {
        const mi = smartPasteMenuItems.find(m => String(m.id) === String(value));
        if (mi) updated[index].matched_item = mi.name;
      }
      return updated;
    });
  };

  const removeSmartRow = (index) => {
    setSmartParsedRows(prev => prev.filter((_, i) => i !== index));
  };

  const submitSmartPaste = async () => {
    const validRows = smartParsedRows.filter(r => r.matched_id && r.total_price);
    if (!validRows.length) { toast.error('No valid rows to submit'); return; }
    if (!smartPasteDate) { toast.error('Select a sale date'); return; }

    setSmartSubmitting(true);
    try {
      const grouped = {};
      for (const row of validRows) {
        const key = `${smartPasteDate}|${row.time || '12:00'}`;
        if (!grouped[key]) grouped[key] = { date: smartPasteDate, time: row.time || '12:00', items: [] };
        grouped[key].items.push({
          menu_item_id: parseInt(row.matched_id),
          quantity: parseInt(row.quantity) || 1,
          unit_price: parseFloat(row.total_price) / (parseInt(row.quantity) || 1),
        });
      }

      let created = 0;
      for (const g of Object.values(grouped)) {
        await api.post('/orders/add_previous_sale/', {
          sale_date: g.date,
          sale_time: g.time,
          payment_method: 'cash',
          items: g.items,
        });
        created++;
      }
      toast.success(`${created} sale(s) added`);
      setShowSmartPaste(false);
      setSmartPasteText('');
      setSmartParsedRows([]);
      setSmartPasteDate('');
      fetchPreviousSales();
      fetchReport();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to submit'));
    } finally {
      setSmartSubmitting(false);
    }
  };

  const fetchExpenseBasedPL = async () => {
    setExpensePLLoading(true);
    try {
      const now = new Date();
      let sd, ed;
      if (expensePLPeriod === 'monthly') {
        sd = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
        ed = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      } else {
        sd = `${now.getFullYear()}-01-01`;
        ed = `${now.getFullYear()}-12-31`;
      }
      if (startDate && endDate) {
        sd = startDate;
        ed = endDate;
      }
      let url = `/reports/profit-expense-based/?start_date=${sd}&end_date=${ed}`;
      if (departmentFilter) url += `&department_id=${departmentFilter}`;
      const res = await api.get(url);
      setExpensePLData(res.data);
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to load expense-based P&L'));
    } finally {
      setExpensePLLoading(false);
    }
  };

  useEffect(() => {
    if (activeReport === 'profit' && profitMode === 'expense') {
      fetchExpenseBasedPL();
    }
  }, [activeReport, profitMode, expensePLPeriod, startDate, endDate, departmentFilter]);

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text(`${reports.find(r => r.id === activeReport)?.label}`, 14, 22);
    doc.setFontSize(10);
    doc.text(`Period: ${formatUiDate(startDate)} to ${formatUiDate(endDate)}`, 14, 30);
    
    if (activeReport === 'sales' && data) {
      doc.autoTable({
        startY: 40,
        head: [['Metric', 'Value']],
        body: [
          ['Total Sales', formatUiCurrencyCompact(data.summary?.total_sales || 0)],
          ['Total Orders', data.summary?.total_orders || 0],
          ['Average Order Value', formatUiCurrencyCompact(data.summary?.avg_order_value || 0)],
        ],
      });
      
      if (data.items_sold?.length) {
        doc.autoTable({
          startY: doc.lastAutoTable.finalY + 10,
          head: [['Item', 'Category', 'Quantity', 'Total']],
          body: data.items_sold.map(item => [
            item.menu_item__name,
            item.menu_item__category__name,
            item.qty,
            formatUiCurrencyCompact(item.total)
          ]),
        });
      }
    }
    
    if (activeReport === 'inventory' && data) {
      doc.autoTable({
        startY: 40,
        head: [['Item', 'Quantity', 'Unit Price', 'Value', 'Status']],
        body: data.all_items?.map(item => [
          item.name,
          `${item.quantity} ${item.unit_abbr}`,
          formatUiCurrencyCompact(item.purchase_price),
          formatUiCurrencyCompact(parseFloat(item.quantity) * parseFloat(item.purchase_price)),
          item.is_low_stock ? 'Low Stock' : 'OK'
        ]) || [],
      });
    }
    
    if (activeReport === 'profit' && data) {
      doc.autoTable({
        startY: 40,
        head: [['Metric', 'Value']],
        body: [
          ['Total Revenue', formatUiCurrencyCompact(data.summary?.total_revenue || 0)],
          ['Total Cost', formatUiCurrencyCompact(data.summary?.total_cost || 0)],
          ['Gross Profit', formatUiCurrencyCompact(data.summary?.gross_profit || 0)],
          ['Wastage Cost', formatUiCurrencyCompact(data.summary?.wastage_cost || 0)],
          ['Total Expenses', formatUiCurrencyCompact(data.summary?.total_expenses || 0)],
          ['Net Profit', formatUiCurrencyCompact(data.summary?.net_profit || 0)],
          ['Gross Margin', `${data.summary?.gross_margin || 0}%`],
          ['Net Margin', `${data.summary?.net_margin || 0}%`],
        ],
      });
    }
    
    if (activeReport === 'expenses' && data) {
      doc.autoTable({
        startY: 40,
        head: [['Metric', 'Value']],
        body: [
          ['Total Expenses', formatUiCurrencyCompact(data.summary?.total_expenses || 0)],
          ['Total Entries', data.summary?.total_entries || 0],
          ['Cash', formatUiCurrencyCompact(data.summary?.cash_total || 0)],
          ['Digital', formatUiCurrencyCompact(data.summary?.digital_total || 0)],
          ['Card', formatUiCurrencyCompact(data.summary?.card_total || 0)],
        ],
      });
      if (data.by_category?.length) {
        doc.autoTable({
          startY: doc.lastAutoTable.finalY + 10,
          head: [['Category', 'Entries', 'Total']],
          body: data.by_category.map(c => [c.category_display || c.category, c.count, formatUiCurrencyCompact(c.total)]),
        });
      }
      if (data.by_department?.length) {
        doc.autoTable({
          startY: doc.lastAutoTable.finalY + 10,
          head: [['Department', 'Entries', 'Total']],
          body: data.by_department.map(d => [d.department__name, d.count, formatUiCurrencyCompact(d.total)]),
        });
      }
    }
    
    doc.save(`${activeReport}_report_${startDate}_to_${endDate}.pdf`);
    toast.success('PDF exported');
  };

  const exportExcel = () => {
    let exportData = [];
    
    if (activeReport === 'sales' && data) {
      exportData = data.items_sold?.map(item => ({
        Item: item.menu_item__name,
        Category: item.menu_item__category__name,
        Quantity: item.qty,
        Total: item.total
      })) || [];
    }

    if (activeReport === 'staff' && data) {
      exportData = data.staff_summary?.map(rawItem => {
        const item = roundStaffSalaryRow(rawItem);
        return {
          Staff: item.staff_name,
          Code: item.staff_code,
          'Monthly Salary': item.monthly_salary,
          'Payable Salary': item.payable_salary,
          Deductions: item.deduction_paid,
          Bonus: item.bonus_paid,
          'Net Salary': item.net_salary,
          'Total Paid': item.total_paid,
        };
      }) || [];
    }
    
    if (activeReport === 'inventory' && data) {
      exportData = data.all_items?.map(item => ({
        Name: item.name,
        Quantity: item.quantity,
        Unit: item.unit_abbr,
        'Unit Price': item.purchase_price,
        Value: parseFloat(item.quantity) * parseFloat(item.purchase_price),
        Status: item.is_low_stock ? 'Low Stock' : 'OK'
      })) || [];
    }
    
    if (activeReport === 'profit' && data) {
      exportData = data.by_item?.map(item => ({
        Item: item.name,
        Quantity: item.qty,
        Revenue: item.revenue,
        Cost: item.cost,
        Profit: item.profit,
        'Margin %': item.margin
      })) || [];
    }
    
    if (activeReport === 'wastage' && data) {
      exportData = data.by_item?.map(item => ({
        Item: item.raw_material__name,
        'Wastage Count': item.count,
        Quantity: item.qty,
        'Cost Impact': item.cost
      })) || [];
    }
    
    if (activeReport === 'expenses' && data) {
      exportData = [
        ...(data.by_category || []).map(c => ({
          Type: 'By Category',
          Name: c.category_display || c.category,
          Entries: c.count,
          Total: c.total
        })),
        ...(data.by_department || []).map(d => ({
          Type: 'By Department',
          Name: d.department__name,
          Entries: d.count,
          Total: d.total
        }))
      ];
    }
    
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Report');
    XLSX.writeFile(wb, `${activeReport}_report_${startDate}_to_${endDate}.xlsx`);
    toast.success('Excel exported');
  };

  const formatCurrency = (amount) => formatUiCurrencyCompact(amount);
  const formatRoundedSalaryCurrency = (amount) => formatUiCurrencyCompact(roundSalaryDisplayAmount(amount));
  const roundStaffSalaryRow = (item = {}) => ({
    ...item,
    monthly_salary: roundSalaryDisplayAmount(item.monthly_salary || 0),
    payable_salary: roundSalaryDisplayAmount(item.payable_salary || 0),
    deduction_paid: roundSalaryDisplayAmount(item.deduction_paid || 0),
    bonus_paid: roundSalaryDisplayAmount(item.bonus_paid || 0),
    net_salary: roundSalaryDisplayAmount(item.net_salary || 0),
    total_paid: roundSalaryDisplayAmount(item.total_paid || 0),
  });

  const getDailyTrendData = () => {
    if (datePreset === 'today') return normalizedHourlySales;
    const rawData = (data?.daily_sales || []).map(d => ({ ...d, total: parseFloat(d.total || 0) }));
    if (datePreset === 'week') {
      const start = new Date(startDate + 'T00:00:00');
      return Array.from({ length: 7 }, (_, i) => {
        const d = new Date(start); d.setDate(start.getDate() + i);
        const ds = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
        const found = rawData.find(r => r.date && String(r.date).startsWith(ds));
        return { date: ds, total: found?.total || 0, count: found?.count || 0 };
      });
    }
    if (datePreset === 'month') {
      const d0 = new Date(startDate + 'T00:00:00');
      const days = new Date(d0.getFullYear(), d0.getMonth() + 1, 0).getDate();
      return Array.from({ length: days }, (_, i) => {
        const ds = `${d0.getFullYear()}-${String(d0.getMonth()+1).padStart(2,'0')}-${String(i+1).padStart(2,'0')}`;
        const found = rawData.find(r => r.date && String(r.date).startsWith(ds));
        return { date: ds, total: found?.total || 0, count: found?.count || 0 };
      });
    }
    if (datePreset === 'year') {
      // Backend returns daily points; roll them up into 12 monthly buckets for the year view.
      const start = new Date(startDate + 'T00:00:00');
      const monthMap = new Map();
      rawData.forEach(r => {
        const key = String(r.date || '').slice(0, 7);
        if (!key) return;
        const m = monthMap.get(key) || { total: 0, count: 0 };
        m.total += parseFloat(r.total || 0); m.count += (r.count || 0);
        monthMap.set(key, m);
      });
      return Array.from({ length: 12 }, (_, i) => {
        const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
        const mk = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        const found = monthMap.get(mk) || { total: 0, count: 0 };
        return { date: `${mk}-01`, total: found.total, count: found.count };
      });
    }
    return rawData;
  };

  const salesCtx = {
    data, formatCurrency, periodParts, openChartTable, getDailyTrendData, datePreset, openDrillDown, normalizedHourlySales,
    COLORS, formatRangeDateTick,
    showPreviousSales, setShowPreviousSales, setShowAddPrevSale, setShowSmartPaste,
    previousSalesLoading, previousSales, previousSalesSummary, expandedPrevDay, setExpandedPrevDay, viewPreviousSaleReceipt, deletePreviousSale,
    showAddPrevSale, prevSaleDate, setPrevSaleDate, prevSaleTime, setPrevSaleTime, prevSalePayment, setPrevSalePayment,
    prevSaleItems, addPrevSaleItem, updatePrevSaleItem, removePrevSaleItem, submitPreviousSale, prevSaleSubmitting, menuItems,
    showSmartPaste, smartPasteDate, setSmartPasteDate, smartParsedRows, smartPasteText, setSmartPasteText, parseSmartPaste, smartParsing,
    updateSmartRow, smartPasteMenuItems, removeSmartRow, setSmartParsedRows, submitSmartPaste, smartSubmitting,
  };
  const renderSalesReport = () => <SalesReport {...salesCtx} />;


  const handleReportTabChange = (reportId) => {
    setActiveReport(reportId);
    const params = new URLSearchParams(location.search);
    params.set('tab', reportId);
    navigate({ pathname: location.pathname, search: `?${params.toString()}` });
  };

  const inventoryCtx = { data, formatCurrency, openDrillDown };
  const renderInventoryReport = () => <InventoryReport {...inventoryCtx} />;

  const profitCtx = { data, formatCurrency, profitMode, setProfitMode, expensePLData, expensePLLoading, expensePLPeriod, setExpensePLPeriod, setDepartmentFilter };
  const renderProfitReport = () => <ProfitReport {...profitCtx} />;


  const expensesCtx = { data, formatCurrency, openChartTable, expenseDailyTrend, openDrillDown, datePreset, formatRangeDateTick, COLORS };
  const renderExpensesReport = () => <ExpensesReport {...expensesCtx} />;





  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Reports</h1>
          <p className="text-sm sm:text-base text-gray-500">Comprehensive business analytics</p>
        </div>
        {activeReport !== 'stock_suggestion' && (
        <div className="flex gap-2">
          <button onClick={exportPDF} className="bg-red-600 hover:bg-red-700 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center gap-2 text-sm sm:text-base">
            <FileText size={18} /> PDF
          </button>
          <button onClick={exportExcel} className="bg-green-600 hover:bg-green-700 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center gap-2 text-sm sm:text-base">
            <Download size={18} /> Excel
          </button>
        </div>
        )}
      </div>

      {/* Report Tabs */}
      <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4">
        <UiTabs tabs={reports} active={activeReport} onChange={handleReportTabChange} />
      </div>

      {/* Date & Dimension Filters */}
      {activeReport !== 'stock_suggestion' && (
      <UiDateRangeBlock
        preset={datePreset}
        presetOptions={buildStandardPresetOptions(Object.keys(DATE_PRESETS))}
        onPresetChange={applyDatePreset}
        onPrevious={() => shiftRange(-1)}
        onNext={() => shiftRange(1)}
        fromDate={startDate}
        onFromDateChange={setStartDate}
        toDate={filterEndCalendarDate}
        onToDateChange={(value) => setEndDate(addCalendarDays(value, -1))}
        fromTime={startTime}
        onFromTimeChange={setStartTime}
        toTime={endTime}
        onToTimeChange={setEndTime}
        rangeText={`Selected range: ${selectedRangeLabel}`}
      >
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
          {(activeReport === 'sales' || activeReport === 'staff') && (
            <>
              <select value={departmentFilter} onChange={(e) => { setDepartmentFilter(e.target.value); setCategoryFilter(''); setMenuItemFilter(''); setModifierFilter(''); setModifierOptionFilter(''); }} className="w-full px-3 py-2 border rounded-lg text-sm sm:w-auto">
                <option value="">All Departments</option>
                {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
              {activeReport === 'sales' && (
                <>
                  <select value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setMenuItemFilter(''); setModifierFilter(''); setModifierOptionFilter(''); }} className="w-full px-3 py-2 border rounded-lg text-sm sm:w-auto">
                    <option value="">All Categories</option>
                    {categories.filter(c => !departmentFilter || String(c.department) === departmentFilter).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <select value={menuItemFilter} onChange={(e) => { setMenuItemFilter(e.target.value); setModifierFilter(''); setModifierOptionFilter(''); }} className="w-full px-3 py-2 border rounded-lg text-sm sm:w-auto">
                    <option value="">All Menu Items</option>
                    {menuItems.filter(m => !categoryFilter || String(m.category) === categoryFilter).map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                  <select value={modifierFilter} onChange={(e) => { setModifierFilter(e.target.value); setModifierOptionFilter(''); }} disabled={!menuItemFilter || reportModifierOptions.length === 0} className="w-full px-3 py-2 border rounded-lg text-sm disabled:bg-gray-100 disabled:text-gray-400 sm:w-auto">
                    <option value="">All Modifiers</option>
                    {reportModifierOptions.map(m => <option key={m.id} value={m.id}>{m.display_name || m.name}</option>)}
                  </select>
                  <select value={modifierOptionFilter} onChange={(e) => setModifierOptionFilter(e.target.value)} disabled={!modifierFilter || reportModifierOptionOptions.length === 0} className="w-full px-3 py-2 border rounded-lg text-sm disabled:bg-gray-100 disabled:text-gray-400 sm:w-auto">
                    <option value="">All Modifier Options</option>
                    {reportModifierOptionOptions.map(option => <option key={option.id} value={option.id}>{option.name}</option>)}
                  </select>
                  <select value={servingSizeFilter} onChange={(e) => setServingSizeFilter(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm sm:w-auto">
                    <option value="">All Serving Sizes</option>
                    {Array.from(new Set(servingSizeOptions.map(o => o.name).filter(Boolean))).sort().map(name => <option key={name} value={name}>{name}</option>)}
                  </select>
                </>
              )}
            </>
          )}
          <button onClick={fetchReport} className="self-start rounded-xl border border-gray-200 p-2 text-primary-600 hover:bg-primary-50">
            <RefreshCw size={18} />
          </button>
        </div>
      </UiDateRangeBlock>
      )}

      {activeReport === 'sales' && (
        <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search menu items or modifier options..."
              value={salesSearchQuery}
              onChange={(e) => setSalesSearchQuery(e.target.value)}
              className="w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
            />
            {salesSearchQuery && (
              <button
                onClick={() => setSalesSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X size={16} />
              </button>
            )}
          </div>
          {salesSearchQuery.trim() && (
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-3 bg-gray-50">
                <h3 className="text-sm font-semibold text-gray-800">Search Results</h3>
                <span className="text-xs text-gray-500">{salesSearchRows.length} item{salesSearchRows.length !== 1 ? 's' : ''}</span>
              </div>
              {salesSearchRows.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-white border-b border-gray-100">
                      <tr>
                        <SortableHeader label="Item Name" col="item_name" sortKey={salesSearchSortKey} sortDir={salesSearchSortDir} onSort={handleSalesSearchSort} />
                        <SortableHeader label="Modifier / Options Found" col="modifierLabelsText" sortKey={salesSearchSortKey} sortDir={salesSearchSortDir} onSort={handleSalesSearchSort} />
                        <SortableHeader label="Count" col="count" sortKey={salesSearchSortKey} sortDir={salesSearchSortDir} onSort={handleSalesSearchSort} right />
                        <SortableHeader label="Total" col="total" sortKey={salesSearchSortKey} sortDir={salesSearchSortDir} onSort={handleSalesSearchSort} right />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {salesSearchRows.map((row, index) => (
                        <tr
                          key={`${row.menu_item_id || row.item_name}-${index}`}
                          onClick={() => openDrillDown('top_selling', { item_name: row.item_name }, `Item: ${row.item_name}`)}
                          className="hover:bg-gray-50 cursor-pointer"
                        >
                          <td className="px-4 py-3 font-medium text-gray-800">{row.item_name}</td>
                          <td className="px-4 py-3 text-gray-600">{(row.modifier_labels || []).length ? row.modifier_labels.join(', ') : 'No modifier option'}</td>
                          <td className="px-4 py-3 text-right text-gray-700">{Number(row.count || 0).toLocaleString()}</td>
                          <td className="px-4 py-3 text-right font-semibold text-primary-600">{formatCurrency(row.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="px-4 py-6 text-sm text-gray-500 text-center">No matching sale items found</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Report Content */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <>
          {activeReport === 'sales' && renderSalesReport()}
          {activeReport === 'inventory' && renderInventoryReport()}
          {activeReport === 'profit' && renderProfitReport()}
          {activeReport === 'expenses' && renderExpensesReport()}
        </>
      )}

      {/* Drill Down Modal */}
      <DashboardDrillDown
        isOpen={drillDown.open}
        onClose={closeDrillDown}
        drillType={drillDown.type}
        params={drillDown.params}
        title={drillDown.title}
        apiUrl="/reports/drill-down/"
        onOrderClick={openOrderFromNumber}
        onRowClick={handleReportDrillDownRowClick}
        zIndexClass="z-[60]"
      />

      {historicalOrder && (
        <OrderReceiptModal
          order={historicalOrder}
          settings={reportSettings}
          onClose={() => setHistoricalOrder(null)}
        />
      )}

      {/* Chart Data Table Modal */}
      <DashboardDrillDown
        isOpen={chartTable.open}
        onClose={closeChartTable}
        drillType=""
        params={{}}
        title={chartTable.title}
        directData={chartTable.data}
        directSummary={chartTable.summary}
        onOrderClick={openOrderFromNumber}
        onRowClick={chartTable.onRowClick}
      />
    </div>
  );
};

export default Reports;
